import { Suspense, createElement, lazy, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import SuperAdminProtectedRoute from "./components/SuperAdminProtectedRoute";
import StaffProtectedRoute from "./components/StaffProtectedRoute";
import StartupReady from "./components/startup/StartupReady";
import { useUser } from "./contexts/UserContext";
import { useAppearance } from "./contexts/AppearanceContext";
import PrivateRouteSeo from "./features/marketing/seo/PrivateRouteSeo";
import { publicPages } from "./features/marketing/seo/publicPages";

const Home = lazy(() => import("./pages/Home"));
const SubscriptionBanner = lazy(() => import("./components/SubscriptionBanner"));
const AppearanceTransition = lazy(() => import("./features/appearance/AppearanceTransition"));
const MarketingPage = lazy(() => import("./features/marketing/components/MarketingPage"));
const PersistentAppAssistant = lazy(() => import("./features/assistant/components/PersistentAppAssistant"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const ClassicSignup = lazy(() => import("./pages-classic/auth/Signup"));
const EmailVerification = lazy(() => import("./pages/auth/EmailVerification"));
const ClassicEmailVerification = lazy(() => import("./pages-classic/auth/EmailVerification"));
const ChangeTemporaryPassword = lazy(() => import("./pages/auth/ChangeTemporaryPassword"));
const Login = lazy(() => import("./pages/auth/Login"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ClassicForgotPassword = lazy(() => import("./pages-classic/auth/ForgotPassword"));
const ForgotPasswordOTP = lazy(() => import("./pages/auth/ForgotPasswordOTP"));
const ClassicForgotPasswordOTP = lazy(() => import("./pages-classic/auth/ForgotPasswordOTP"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const ClassicResetPassword = lazy(() => import("./pages-classic/auth/ResetPassword"));
const GoogleCallback = lazy(() => import("./pages/auth/GoogleCallback"));
const GoogleSuccess = lazy(() => import("./pages/auth/GoogleSuccess"));
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const UserDashboard = lazy(() => import("./pages/dashboard/UserDashboard"));
const ClassicDashboard = lazy(() => import("./pages-classic/dashboard/Dashboard"));
const ClassicUserDashboard = lazy(() => import("./pages-classic/dashboard/UserDashboard"));
const Customers = lazy(() => import("./pages/customers/Customers"));
const ClassicCustomers = lazy(() => import("./pages-classic/customers/Customers"));
const Suppliers = lazy(() => import("./pages/suppliers/Suppliers"));
const ClassicSuppliers = lazy(() => import("./pages-classic/suppliers/Suppliers"));
const Inventory = lazy(() => import("./pages/inventory/Inventory"));
const ClassicInventory = lazy(() => import("./pages-classic/inventory/Inventory"));
const Sales = lazy(() => import("./pages/sales/Sales"));
const ClassicSales = lazy(() => import("./pages-classic/sales/Sales"));
const SalesReturns = lazy(() => import("./pages/sales/SalesReturns"));
const ClassicSalesReturns = lazy(() => import("./pages-classic/sales/SalesReturns"));
const SalesReturnDetail = lazy(() => import("./pages/sales/SalesReturnDetail"));
const ClassicSalesReturnDetail = lazy(() => import("./pages-classic/sales/SalesReturnDetail"));
const PurchaseReturns = lazy(() => import("./pages/purchases/PurchaseReturns"));
const ClassicPurchaseReturns = lazy(() => import("./pages-classic/purchases/PurchaseReturns"));
const Payments = lazy(() => import("./pages/payments/Payments"));
const ClassicPayments = lazy(() => import("./pages-classic/payments/Payments"));
const Purchases = lazy(() => import("./pages/purchases/Purchases"));
const ClassicPurchases = lazy(() => import("./pages-classic/purchases/Purchases"));
const Expenses = lazy(() => import("./pages/expenses/Expenses"));
const ClassicExpenses = lazy(() => import("./pages-classic/expenses/Expenses"));
const Settings = lazy(() => import("./pages/Settings/Settings"));
const ClassicSettings = lazy(() => import("./pages-classic/Settings/Settings"));
const FinancialReports = lazy(() => import("./pages/reports/FinancialReports"));
const ClassicFinancialReports = lazy(() => import("./pages-classic/reports/FinancialReports"));
const Materials = lazy(() => import("./pages/materials/Materials"));
const ClassicMaterials = lazy(() => import("./pages-classic/materials/Materials"));
const Production = lazy(() => import("./pages/production/Production"));
const ClassicProduction = lazy(() => import("./pages-classic/production/Production"));
const Subscription = lazy(() => import("./pages/subscription/Subscription"));
const ClassicSubscription = lazy(() => import("./pages-classic/subscription/Subscription"));
const SuperAdminDashboard = lazy(() => import("./pages/superadmin/dashboard/SuperAdminDashboard"));
const SuperAdminTickets = lazy(() => import("./pages/superadmin/tickets/SupportTickets"));
const SuperAdminLiveChat = lazy(() => import("./pages/superadmin/chat/LiveChat"));
const SuperAdminUsers = lazy(() => import("./pages/superadmin/users/Users"));
const SuperAdminUserDetail = lazy(() => import("./pages/superadmin/users/UserDetail"));
const SuperAdminCompanies = lazy(() => import("./pages/superadmin/companies/Companies"));
const SuperAdminSubscriptions = lazy(() => import("./pages/superadmin/subscriptions/Subscriptions"));
const SuperAdminSettings = lazy(() => import("./pages/superadmin/settings/Settings"));
const StaffDashboard = lazy(() => import("./pages/staff/StaffDashboard"));
const ClassicStaffDashboard = lazy(() => import("./interfaces/classic-pages/StaffDashboard"));
const StaffChat = lazy(() => import("./pages/staff/StaffChat"));
const StaffComplaints = lazy(() => import("./pages/staff/StaffComplaints"));
const StaffUsers = lazy(() => import("./pages/staff/StaffUsers"));
const StaffProfile = lazy(() => import("./pages/staff/StaffProfile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AppearanceOnboarding = lazy(() => import("./features/appearance/AppearanceOnboarding"));
const FirstRunAppearanceSetup = lazy(() => import("./features/appearance/FirstRunAppearanceSetup"));
const CompanyProfileOnboarding = lazy(() => import("./features/onboarding/company/CompanyProfileOnboarding"));

function RouteFallback() {
  return <div className="fixed inset-x-0 top-0 z-[60] h-0.5 overflow-hidden bg-blue-100" role="progressbar" aria-label="Opening page"><span className="block h-full w-1/2 animate-[route-progress_1s_ease-in-out_infinite] bg-blue-600" /></div>;
}

function AppearancePage({ modern: ModernPage, classic: ClassicPage }) {
  const { interfaceStyle, isSwitchingInterface, markInterfaceReady } = useAppearance();

  useEffect(() => {
    if (!isSwitchingInterface) return undefined;
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => markInterfaceReady(interfaceStyle));
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [interfaceStyle, isSwitchingInterface, markInterfaceReady]);

  return createElement(interfaceStyle === "classic" ? ClassicPage : ModernPage);
}

function App() {
  const { isAuthenticated } = useUser();
  const { isSwitchingInterface } = useAppearance();

  return (
    <>
      <PrivateRouteSeo />
      {/* Show subscription banner on all authenticated pages */}
      {isAuthenticated ? <Suspense fallback={null}><SubscriptionBanner /></Suspense> : null}
      {isSwitchingInterface ? <Suspense fallback={null}><AppearanceTransition /></Suspense> : null}
      <Suspense fallback={<RouteFallback />}>
      {isAuthenticated ? <AppearanceOnboarding /> : null}
      {isAuthenticated ? <CompanyProfileOnboarding /> : null}
      {isAuthenticated ? <PersistentAppAssistant /> : null}
      <Routes>
        <Route path="/" element={<Home />} />
        {publicPages.filter((page) => page.path !== "/").map((page) => <Route key={page.path} path={page.path} element={<MarketingPage page={page} />} />)}
        <Route path="/signup" element={<AppearancePage modern={Signup} classic={ClassicSignup} />} />
        <Route path="/email-verification" element={<AppearancePage modern={EmailVerification} classic={ClassicEmailVerification} />} />
        <Route
          path="/welcome/interface"
          element={
            <ProtectedRoute allowWhenExpired={true} requiresSubscription={false}>
              <FirstRunAppearanceSetup />
            </ProtectedRoute>
          }
        />
        <Route
          path="/change-temporary-password"
          element={
            <ProtectedRoute allowWhenExpired={true} requiresSubscription={false}>
              <ChangeTemporaryPassword />
            </ProtectedRoute>
          }
        />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<AppearancePage modern={ForgotPassword} classic={ClassicForgotPassword} />} />
        <Route path="/forgot-password/verify" element={<AppearancePage modern={ForgotPasswordOTP} classic={ClassicForgotPasswordOTP} />} />
        <Route path="/forgot-password/reset" element={<AppearancePage modern={ResetPassword} classic={ClassicResetPassword} />} />
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
        <Route path="/auth/google-success" element={<GoogleSuccess />} />
        
        {/* SuperAdmin Login */}
        <Route path="/superadmin/login" element={<Login />} />
        
        {/* SuperAdmin Routes */}
        <Route
          path="/superadmin/dashboard"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminDashboard />
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/superadmin/tickets"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminTickets />
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/superadmin/live-chat"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminLiveChat />
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/superadmin/users"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminUsers />
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/superadmin/users/:id"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminUserDetail />
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/superadmin/companies"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminCompanies />
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/superadmin/subscriptions"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminSubscriptions />
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/superadmin/settings"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminSettings />
            </SuperAdminProtectedRoute>
          }
        />
        
        {/* Staff Routes - uses same login as SuperAdmin, redirects based on role */}
        <Route
          path="/staff"
          element={<StaffProtectedRoute><AppearancePage modern={StaffDashboard} classic={ClassicStaffDashboard} /></StaffProtectedRoute>}
        />
        <Route
          path="/staff/chat"
          element={<StaffProtectedRoute><StaffChat /></StaffProtectedRoute>}
        />
        <Route
          path="/staff/complaints"
          element={<StaffProtectedRoute><StaffComplaints /></StaffProtectedRoute>}
        />
        <Route
          path="/staff/users"
          element={<StaffProtectedRoute><StaffUsers /></StaffProtectedRoute>}
        />
        <Route
          path="/staff/profile"
          element={<StaffProtectedRoute><StaffProfile /></StaffProtectedRoute>}
        />
        
        {/* Dashboard - role-based routing */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowWhenExpired={true} requiresSubscription={false} moduleKey="dashboard">
              <AppearancePage modern={Dashboard} classic={ClassicDashboard} />
            </ProtectedRoute>
          }
        />
        
        {/* User Dashboard - for staff/user role */}
        <Route
          path="/user-dashboard"
          element={
            <ProtectedRoute allowWhenExpired={true} requiresSubscription={false} moduleKey="dashboard">
              <AppearancePage modern={UserDashboard} classic={ClassicUserDashboard} />
            </ProtectedRoute>
          }
        />
        
        {/* Always accessible routes - remain open even after expiration */}
        <Route
          path="/customers"
          element={
            <ProtectedRoute moduleKey="customers">
              <AppearancePage modern={Customers} classic={ClassicCustomers} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/suppliers"
          element={
            <ProtectedRoute moduleKey="suppliers">
              <AppearancePage modern={Suppliers} classic={ClassicSuppliers} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory"
          element={
            <ProtectedRoute moduleKey="inventory">
              <AppearancePage modern={Inventory} classic={ClassicInventory} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/materials"
          element={
            <ProtectedRoute moduleKey="materials">
              <AppearancePage modern={Materials} classic={ClassicMaterials} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/production"
          element={
            <ProtectedRoute requiresSubscription={true} moduleKey="production">
              <AppearancePage modern={Production} classic={ClassicProduction} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute allowWhenExpired={true} requiresSubscription={false} moduleKey="settings">
              <AppearancePage modern={Settings} classic={ClassicSettings} />
            </ProtectedRoute>
          }
        />
        
        {/* Locked routes - require active subscription */}
        <Route
          path="/sales"
          element={
            <ProtectedRoute requiresSubscription={true} moduleKey="sales">
              <AppearancePage modern={Sales} classic={ClassicSales} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales-returns"
          element={
            <ProtectedRoute requiresSubscription={true} moduleKey="sales">
              <AppearancePage modern={SalesReturns} classic={ClassicSalesReturns} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales-returns/:id"
          element={
            <ProtectedRoute requiresSubscription={true} moduleKey="sales">
              <AppearancePage modern={SalesReturnDetail} classic={ClassicSalesReturnDetail} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payments"
          element={
            <ProtectedRoute requiresSubscription={true} moduleKey="payments">
              <AppearancePage modern={Payments} classic={ClassicPayments} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/purchases"
          element={
            <ProtectedRoute requiresSubscription={true} moduleKey="purchases">
              <AppearancePage modern={Purchases} classic={ClassicPurchases} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/purchase-returns"
          element={
            <ProtectedRoute requiresSubscription={true} moduleKey="purchases">
              <AppearancePage modern={PurchaseReturns} classic={ClassicPurchaseReturns} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses"
          element={
            <ProtectedRoute requiresSubscription={true} moduleKey="expenses">
              <AppearancePage modern={Expenses} classic={ClassicExpenses} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute requiresSubscription={true} moduleKey="reports">
              <AppearancePage modern={FinancialReports} classic={ClassicFinancialReports} />
            </ProtectedRoute>
          }
        />
        
        {/* Subscription page - requires auth but not subscription */}
        <Route
          path="/subscription"
          element={
            <ProtectedRoute allowWhenExpired={true} requiresSubscription={false}>
              <AppearancePage modern={Subscription} classic={ClassicSubscription} />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <StartupReady />
      </Suspense>
    </>
  );
}

export default App;

